"use client";

import { useLazyQuery } from "@apollo/client";
import { useAtomValue } from "jotai";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

import { analyticsIdQuery } from "@/components/model/user/_graphql";

import { authAtom } from "@/globalState";
import {
  clearAnalyticsUserId,
  hasAnalyticsUserId,
  isAnalyticsUserIdEnabled,
  setAnalyticsUserId,
} from "@/libs/google";

/**
 * 認証状態に合わせて GA4 の user_id（分析専用の仮名ID）を設定・クリアする。
 * - 認証済みで未設定なら、ページ表示ごとに1回だけ取得を試みる（失敗・未発行なら設定しない）
 * - 認証が外れたらクリアする（セッション失効時は Apollo の認証エラー処理でもクリアする）
 */
export function AnalyticsUserId() {
  const isLogin = useAtomValue(authAtom);
  const pathname = usePathname();
  const [fetchAnalyticsId] = useLazyQuery(analyticsIdQuery, {
    fetchPolicy: "network-only",
  });
  // 認証が外れるたびに進める。取得中に認証が変わったら、古い応答を捨てる
  const sessionGenerationRef = useRef(0);
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!isLogin) {
      sessionGenerationRef.current += 1;
      fetchingRef.current = false;
      clearAnalyticsUserId();
      return;
    }
    if (
      !isAnalyticsUserIdEnabled() ||
      hasAnalyticsUserId() ||
      fetchingRef.current
    ) {
      return;
    }

    const generation = sessionGenerationRef.current;
    fetchingRef.current = true;
    fetchAnalyticsId()
      .then(({ data }) => {
        const analyticsId = data?.analyticsId;
        // 取得中にログアウトした場合（別のワーカーで再ログインした場合を含む）は設定しない
        if (analyticsId && generation === sessionGenerationRef.current) {
          setAnalyticsUserId(analyticsId);
        }
      })
      .catch(() => {
        // 計測のための取得なので、失敗しても画面の動作を妨げない
      })
      .finally(() => {
        if (generation === sessionGenerationRef.current) {
          fetchingRef.current = false;
        }
      });
  }, [isLogin, pathname, fetchAnalyticsId]);

  return null;
}
